import { CreateQuizCompletion, QuizCompletion, UpdateQuizCompletion } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const quizKeys = {
  all: ["quiz"] as const,
  questions: () => [...quizKeys.all, "questions"] as const,
  myData: () => [...quizKeys.all, "me"] as const
};

export function useQuizzes() {
  const { quizApiClient } = useApiClients();

  return useQuery({
    queryKey: quizKeys.questions(),
    queryFn: () => quizApiClient.getQuizzes(),
    staleTime: 10 * 60 * 1000
  });
}

export function useMyQuizData(options: { enabled?: boolean } = {}) {
  const { quizApiClient } = useApiClients();

  return useQuery({
    queryKey: quizKeys.myData(),
    queryFn: () => quizApiClient.getMyQuizData(),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000
  });
}

export function useCompleteQuizMutation(
  options: { onSuccess?: (result: QuizCompletion) => void; onError?: (error: unknown) => void } = {}
) {
  const { quizApiClient } = useApiClients();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateQuizCompletion) => quizApiClient.completeMyQuiz(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: quizKeys.myData() });
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpdateQuizDataMutation(
  options: { onSuccess?: (result: QuizCompletion) => void; onError?: (error: unknown) => void } = {}
) {
  const { quizApiClient } = useApiClients();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateQuizCompletion) => quizApiClient.updateMyQuizData(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: quizKeys.myData() });
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}
